import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga4';
import { isEmail } from 'class-validator';
import LazyLoad from 'react-lazyload';
import { useStyles } from './styles';
import { Box, CircularProgress, Container, TextField, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import CustomButton from 'components/CustomButton';
import ContactService from 'services/ContactService';

const Props = {
  textTitle: PropTypes.string.isRequired,
  textSubtitle: PropTypes.node.isRequired,
  textInformation: PropTypes.node.isRequired,
};

export default function ReferToAcademicInstitution({ textTitle, textSubtitle, textInformation }) {
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [completedSubmission, setCompletedSubmission] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function sendTrackingEvents() {
    ReactGA.event({
      category: 'landing_page',
      action: 'institution_referred',
      label: 'When user click in "Refer Mind the Graph" button',
      value: email,
    });
  }

  function canSubmit() {
    return !!email && isEmail(email);
  }

  function handleInputChange(value) {
    if (!!value && emailError) {
      setEmailError(false);
    }

    setEmail(value);
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!canSubmit()) {
      setEmailError(true);
    } else {
      setIsLoading(true);
      sendTrackingEvents();

      await ContactService.sendContactPersonCharge({ email: email });

      setCompletedSubmission(true);
    }
  }

  return (
    <LazyLoad offset={100}>
      <Box className={classes.mainContentBox} py={6}>
        <Container>
          <Box>
            <Typography className={classes.whiteColor} variant="h4" component="h2" align="center">
              <b>{textTitle}</b>
            </Typography>

            <Typography className={classes.textRefer} align="center">
              {textSubtitle}
            </Typography>

            <Typography className={classes.textInsertEmail} align="center">
              <b>{textInformation}</b>
            </Typography>

            {!completedSubmission && (
              <Box display="flex" justifyContent="center" alignItems="center" pt={6}>
                <form onSubmit={handleSubmit}>
                  <TextField
                    id="email-input"
                    variant="standard"
                    InputProps={{
                      classes: {
                        root: classes.input,
                        focused: classes.inputFocused,
                        input: classes.inputText,
                      },
                    }}
                    InputLabelProps={{
                      classes: {
                        root: classes.inputLabel,
                        focused: classes.inputLabelFocused,
                      },
                    }}
                    placeholder="emailexample@mindthegraph.com"
                    required={true}
                    error={emailError}
                    helperText={emailError ? 'Please fill this field correctly' : undefined}
                    type="email"
                    value={email}
                    onChange={({ target: { value } }) => handleInputChange(value)}
                    aria-label="Email Address"
                    label="Enter your e-mail"
                    title="Enter your email address"
                  />

                  <Box textAlign="center" pt={2}>
                    <CustomButton type="submit" variant="buttonWhiteTextPurple">
                      {!isLoading ? (
                        'Refer Mind the Graph'
                      ) : (
                        <CircularProgress size={24} classes={{ root: classes.primaryColor }} />
                      )}
                    </CustomButton>
                  </Box>
                </form>
              </Box>
            )}

            {completedSubmission && (
              <Box py={2} display="flex" justifyContent="center" alignItems="center">
                <Typography className={classes.whiteColor} variant="h6" component="p">
                  Thanks for referring Mind the Graph. We'll get in touch soon!
                </Typography>
                <Box ml={1}>
                  <Check size={24} classes={{ root: classes.whiteColor }} />
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </Box>
    </LazyLoad>
  );
}

ReferToAcademicInstitution.propTypes = Props;
